import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { CartContext } from '../../App';
import apiInstance from '../../utils/axios';
import SEO from '../../components/SEO';
import UserData from '../plugin/UserData';
import GetCurrentAddress from '../plugin/UserCountry';
import getCartID from '../plugin/cartID';
import Swal from 'sweetalert2';
import {
  Search,
  Truck,
  SlidersHorizontal,
  X,
  Grid3X3,
  LayoutGrid,
  Heart,
  Package,
  ChevronDown,
  ChevronUp,
  Check,
  Sparkles,
  Filter,
  Tag,
  Eye,
  ShoppingCart,
  Star
} from 'lucide-react';

// Fuzzy Search Utilities
const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[m][n];
};

const fuzzyMatch = (text, query) => {
  if (!text || !query) return { match: false, score: 0 };
  
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  
  // Exact match - highest score
  if (textLower.includes(queryLower)) {
    return { match: true, score: 100 };
  }
  
  // Check if query words match in any order
  const queryWords = queryLower.split(/\s+/).filter(w => w.length > 0);
  const textWords = textLower.split(/\s+/);
  
  let matchedWords = 0;
  let fuzzyScore = 0;
  
  for (const queryWord of queryWords) {
    // Skip very short words for fuzzy matching
    if (queryWord.length < 2) continue;
    
    let bestWordScore = 0;
    
    for (const textWord of textWords) {
      // Exact word match
      if (textWord.includes(queryWord) || queryWord.includes(textWord)) {
        bestWordScore = Math.max(bestWordScore, 90);
        continue;
      }
      
      // Calculate similarity for longer words
      if (queryWord.length >= 3) {
        const distance = levenshteinDistance(queryWord, textWord);
        const maxLen = Math.max(queryWord.length, textWord.length);
        const similarity = ((maxLen - distance) / maxLen) * 100;
        
        // Allow up to 2 character typos for shorter words, 3 for longer
        const tolerance = queryWord.length <= 5 ? 2 : 3;
        if (distance <= tolerance && similarity > 50) {
          bestWordScore = Math.max(bestWordScore, similarity);
        }
      }
      
      // First letter match with similar length - likely typo
      if (queryWord[0] === textWord[0] && 
          Math.abs(queryWord.length - textWord.length) <= 2 &&
          queryWord.length >= 3) {
        const distance = levenshteinDistance(queryWord, textWord);
        if (distance <= 3) {
          const similarity = ((Math.max(queryWord.length, textWord.length) - distance) / Math.max(queryWord.length, textWord.length)) * 85;
          bestWordScore = Math.max(bestWordScore, similarity);
        }
      }
    }
    
    if (bestWordScore > 50) {
      matchedWords++;
      fuzzyScore += bestWordScore;
    }
  }
  
  if (matchedWords > 0) {
    const avgScore = fuzzyScore / queryWords.length;
    const coverageBonus = (matchedWords / queryWords.length) * 20;
    return { match: true, score: avgScore + coverageBonus };
  }
  
  // Check for character transposition (common typo)
  if (queryLower.length >= 3) {
    for (const textWord of textWords) {
      if (textWord.length >= 3) {
        const distance = levenshteinDistance(queryLower, textWord);
        const maxLen = Math.max(queryLower.length, textWord.length);
        const similarity = ((maxLen - distance) / maxLen) * 100;
        if (distance <= 2 && similarity > 60) {
          return { match: true, score: similarity };
        }
      }
    }
  }
  
  return { match: false, score: 0 };
};

const Toast = Swal.mixin({
  toast: true,
  position: 'top',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

const Products = () => {
  const { t, currentLanguage } = useLanguage();
  const { formatUSD } = useCurrency();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cartCount, setCartCount] = useContext(CartContext);

  // State
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 50000 });
  const [maxPrice, setMaxPrice] = useState(50000);
  const [sortBy, setSortBy] = useState('relevance');
  const [freeShippingOnly, setFreeShippingOnly] = useState(false);
  const [onSaleOnly, setOnSaleOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    brands: true,
    price: true,
    availability: true,
  });
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });

  const currentAddress = GetCurrentAddress();
  const userData = UserData();
  const cart_id = getCartID();

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await apiInstance.get('products/');
        const data = response.data.results || response.data || [];
        setProducts(data);
        
        if (data.length > 0) {
          const prices = data.map(p => parseFloat(p.price) || 0);
          const max = Math.ceil(Math.max(...prices));
          setMaxPrice(max);
          setPriceRange({ min: 0, max });
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiInstance.get('category/');
        const data = response.data.results || response.data || [];
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch brands
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await apiInstance.get('brands/');
        const data = response.data.results || response.data || [];
        setBrands(data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    fetchBrands();
  }, []);

  // Handle URL params for category filter
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && categories.length > 0) {
      const categoryId = parseInt(categoryParam);
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        const categoryName = category.title_translated || category.title;
        if (!selectedCategories.includes(categoryName)) {
          setSelectedCategories([categoryName]);
        }
      }
    }
  }, [categories, searchParams]);

  // Handle URL params for brand filter
  useEffect(() => {
    const brandParam = searchParams.get('brand');
    if (brandParam && brands.length > 0) {
      const brand = brands.find(b => b.slug === brandParam);
      if (brand) {
        setSelectedBrand(brand.slug);
      }
    }
  }, [brands, searchParams]);

  // Filter products
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.trim();
      
      // Score each product based on fuzzy matching
      const scoredProducts = filtered.map((product) => {
        const title = product.title_translated || product.title || '';
        const desc = product.description_translated || product.description || '';
        const category = product.category?.title_translated || product.category?.title || '';
        
        const titleMatch = fuzzyMatch(title, query);
        const descMatch = fuzzyMatch(desc, query);
        const categoryMatch = fuzzyMatch(category, query);
        
        // Prioritize title matches, then category, then description
        const bestScore = Math.max(
          titleMatch.score * 1.2,  // Title matches weighted higher
          categoryMatch.score * 1.1,
          descMatch.score
        );
        
        const isMatch = titleMatch.match || descMatch.match || categoryMatch.match;
        
        return { product, score: bestScore, isMatch };
      });
      
      // Filter and sort by relevance score
      filtered = scoredProducts
        .filter(item => item.isMatch)
        .sort((a, b) => b.score - a.score)
        .map(item => item.product);
    }

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((product) => {
        const categoryTitle = product.category?.title || '';
        const categoryTranslated = product.category?.title_translated || '';
        return selectedCategories.includes(categoryTitle) || selectedCategories.includes(categoryTranslated);
      });
    }

    // Filter by brand
    if (selectedBrand) {
      filtered = filtered.filter((product) => {
        return product.brand?.slug === selectedBrand;
      });
    }

    filtered = filtered.filter((product) => {
      const price = parseFloat(product.price) || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    if (freeShippingOnly) {
      filtered = filtered.filter((product) => 
        product.shipping_ammount === 0 || 
        product.shipping_ammount === '0.00' ||
        !product.shipping_ammount
      );
    }

    if (onSaleOnly) {
      filtered = filtered.filter((product) => 
        product.old_price && parseFloat(product.old_price) > parseFloat(product.price)
      );
    }

    if (inStockOnly) {
      filtered = filtered.filter((product) => {
        const stock = product.stock_qty || product.stock || product.quantity || 0;
        return stock > 0;
      });
    }

    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price-desc':
        filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'discount':
        filtered.sort((a, b) => {
          const discountA = a.old_price ? (parseFloat(a.old_price) - parseFloat(a.price)) / parseFloat(a.old_price) : 0;
          const discountB = b.old_price ? (parseFloat(b.old_price) - parseFloat(b.price)) / parseFloat(b.old_price) : 0;
          return discountB - discountA;
        });
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
        break;
      default:
        filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
    }

    return filtered;
  }, [products, searchQuery, selectedCategories, priceRange, sortBy, freeShippingOnly, onSaleOnly, inStockOnly]);

  const toggleCategory = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    setSelectedBrand(null);
    setPriceRange({ min: 0, max: maxPrice });
    setFreeShippingOnly(false);
    setOnSaleOnly(false);
    setInStockOnly(false);
    setSearchParams({});
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedCategories.length > 0) count += selectedCategories.length;
    if (selectedBrand) count += 1;
    if (priceRange.min > 0 || priceRange.max < maxPrice) count += 1;
    if (freeShippingOnly) count += 1;
    if (onSaleOnly) count += 1;
    if (inStockOnly) count += 1;
    return count;
  };

  const hasActiveFilters = getActiveFilterCount() > 0;

  const addToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    
    const formdata = new FormData();
    formdata.append('product_id', product.id);
    formdata.append('user_id', userData?.user_id || 0);
    formdata.append('qty', 1);
    formdata.append('price', product.price);
    formdata.append('shipping_ammount', product.shipping_ammount || 0);
    formdata.append('country', currentAddress.country);
    formdata.append('size', 'No Size');
    formdata.append('color', 'No Color');
    formdata.append('cart_id', cart_id);

    try {
      const response = await apiInstance.post('cart-view/', formdata);
      Toast.fire({
        icon: 'success',
        title: response.data.message,
      });
      
      const cartResponse = await apiInstance.get(`cart-list/${cart_id}/`);
      setCartCount(cartResponse.data.length);
    } catch (error) {
      console.error('Error adding to cart:', error);
      Toast.fire({
        icon: 'error',
        title: t('errorAddingToCart') || 'Error al agregar al carrito',
      });
    }
  };

  const addToWishlist = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!userData?.user_id) {
      Toast.fire({
        icon: 'warning',
        title: t('loginRequired') || 'Inicia sesión para agregar a favoritos',
      });
      return;
    }

    try {
      const response = await apiInstance.post('add-to-wishlist/', {
        product_id: product.id,
        user_id: userData.user_id,
      });
      Toast.fire({
        icon: 'success',
        title: t('addedToWishlist') || 'Agregado a favoritos ❤️',
      });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      // If it's a duplicate, still show a friendly message
      if (error.response?.status === 400 || error.response?.data?.error?.includes('already')) {
        Toast.fire({
          icon: 'info',
          title: t('alreadyInWishlist') || 'Ya está en tu lista de favoritos',
        });
      } else {
        Toast.fire({
          icon: 'error',
          title: t('errorAddingToWishlist') || 'Error al agregar a favoritos',
        });
      }
    }
  };

  const handleImageMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({ x, y });
  };

  const handleImageMouseLeave = () => {
    setZoomOrigin({ x: 50, y: 50 });
  };

  const formatPrice = (price) => {
    return formatUSD(price);
  };

  const calculateDiscount = (price, oldPrice) => {
    if (!oldPrice || parseFloat(oldPrice) <= parseFloat(price)) return null;
    return Math.round(((parseFloat(oldPrice) - parseFloat(price)) / parseFloat(oldPrice)) * 100);
  };

  // Get current category name for hero
  const getCurrentCategoryName = () => {
    if (selectedCategories.length === 1) {
      return selectedCategories[0];
    }
    return null;
  };

  // Filter Section Component
  const FilterSection = ({ title, icon: Icon, expanded, onToggle, children }) => (
    <div className="filter-section">
      <div className="filter-section-header" onClick={onToggle}>
        <div className="filter-section-title">
          <Icon size={18} />
          <span>{title}</span>
        </div>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>
      {expanded && <div className="filter-section-content">{children}</div>}
    </div>
  );

  // Checkbox Filter Component
  const CheckboxFilter = ({ label, checked, onChange, count }) => (
    <label className="checkbox-filter" onClick={(e) => { e.preventDefault(); onChange(); }}>
      <div className={`checkbox-box ${checked ? 'checked' : ''}`}>
        {checked && <Check size={14} />}
      </div>
      <span className="checkbox-label">{label}</span>
      {count !== undefined && <span className="checkbox-count">{count}</span>}
    </label>
  );

  // Toggle Filter Component
  const ToggleFilter = ({ label, icon: Icon, checked, onChange }) => (
    <div 
      className={`toggle-filter ${checked ? 'active' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <Icon size={16} />
      <span>{label}</span>
    </div>
  );

  return (
    <>
      <SEO 
        title="Catálogo de Vapes y Perfumes Árabes"
        description="Explora nuestro catálogo completo de vapes, vaporizadores, pods desechables, e-liquids, perfumes árabes, oud, bakhoor y accesorios. Filtra por categoría, precio y más. Envíos a todo Paraguay."
        keywords="catálogo vapes, productos vape paraguay, perfumes árabes catálogo, comprar vape online, pods desechables, e-liquid sabores, oud perfume, bakhoor comprar, accesorios vape, coils, resistencias"
        url="https://asmarshop.com/products"
      />
      <div className="products-page">
        <div className="products-container">
          {/* Mobile Filter Button */}
          <button 
            className="mobile-filter-btn"
            onClick={() => setMobileFiltersOpen(true)}
          >
            <SlidersHorizontal size={18} />
            {t('filters') || 'Filtros'}
            {hasActiveFilters && (
              <span className="filter-badge">{getActiveFilterCount()}</span>
            )}
          </button>

          {/* Mobile Filter Overlay */}
          {mobileFiltersOpen && (
            <div className="mobile-filter-overlay" onClick={() => setMobileFiltersOpen(false)}>
              <div className="mobile-filter-panel" onClick={e => e.stopPropagation()}>
                <div className="mobile-filter-header">
                  <h3>{t('filters') || 'Filtros'}</h3>
                  <button onClick={() => setMobileFiltersOpen(false)}>
                    <X size={24} />
                  </button>
                </div>
                <div className="mobile-filter-content">
                  {/* Availability Toggles */}
                  <div className="availability-toggles">
                    <ToggleFilter
                      label={t('inStock') || "En Stock"}
                      icon={Check}
                      checked={inStockOnly}
                      onChange={setInStockOnly}
                    />
                    <ToggleFilter
                      label={t('freeShipping') || "Envío Gratis"}
                      icon={Truck}
                      checked={freeShippingOnly}
                      onChange={setFreeShippingOnly}
                    />
                    <ToggleFilter
                      label={t('offers') || "En Oferta"}
                      icon={Tag}
                      checked={onSaleOnly}
                      onChange={setOnSaleOnly}
                    />
                  </div>

                  <FilterSection 
                    title={t('categories') || "Categorías"}
                    icon={Grid3X3}
                    expanded={expandedSections.categories}
                    onToggle={() => toggleSection('categories')}
                  >
                    <div className="category-list">
                      <CheckboxFilter
                        label={t('all') || "Todas"}
                        checked={selectedCategories.length === 0}
                        onChange={() => setSelectedCategories([])}
                        count={products.length}
                      />
                      {categories.map((category) => {
                        const categoryName = category.title_translated || category.title;
                        const count = products.filter(p => 
                          p.category?.title === category.title || p.category?.title_translated === categoryName
                        ).length;
                        if (count === 0) return null;
                        return (
                          <CheckboxFilter
                            key={category.id}
                            label={categoryName}
                            checked={selectedCategories.includes(categoryName)}
                            onChange={() => toggleCategory(categoryName)}
                            count={count}
                          />
                        );
                      })}
                    </div>
                  </FilterSection>

                  {/* Mobile Brands Filter */}
                  <FilterSection 
                    title={t('brand') || "Marca"}
                    icon={Tag}
                    expanded={expandedSections.brands}
                    onToggle={() => toggleSection('brands')}
                  >
                    <div className="category-list">
                      <CheckboxFilter
                        label={t('allBrands') || "Todas las Marcas"}
                        checked={!selectedBrand}
                        onChange={() => setSelectedBrand(null)}
                        count={products.length}
                      />
                      {brands.map((brand) => {
                        const brandName = brand.title_pt || brand.title_es || brand.title;
                        const count = products.filter(p => p.brand?.slug === brand.slug).length;
                        if (count === 0) return null;
                        return (
                          <CheckboxFilter
                            key={brand.id}
                            label={brandName}
                            checked={selectedBrand === brand.slug}
                            onChange={() => setSelectedBrand(selectedBrand === brand.slug ? null : brand.slug)}
                            count={count}
                          />
                        );
                      })}
                    </div>
                  </FilterSection>
                </div>
                <div className="mobile-filter-footer">
                  <button className="clear-btn" onClick={clearFilters}>
                    {t('clearFilters') || 'Limpiar Todo'}
                  </button>
                  <button className="apply-btn" onClick={() => setMobileFiltersOpen(false)}>
                    {t('apply') || 'Aplicar Filtros'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="products-layout">
            {/* Sidebar */}
            <aside className="products-sidebar">
              <div className="sidebar-card">
                <div className="sidebar-header">
                  <Filter size={20} />
                  <h2>{t('filters') || 'Filtros'}</h2>
                  {hasActiveFilters && (
                    <button className="clear-all-btn" onClick={clearFilters}>
                      {t('clear') || 'Limpiar'}
                    </button>
                  )}
                </div>

                {/* Search Input */}
                <div className="search-input-wrapper">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder={t('searchPlaceholder') || "Buscar productos..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')}>
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* Quick Availability Toggles */}
                <div className="availability-toggles">
                  <ToggleFilter
                    label={t('inStock') || "En Stock"}
                    icon={Check}
                    checked={inStockOnly}
                    onChange={setInStockOnly}
                  />
                  <ToggleFilter
                    label={t('freeShipping') || "Envío Gratis"}
                    icon={Truck}
                    checked={freeShippingOnly}
                    onChange={setFreeShippingOnly}
                  />
                  <ToggleFilter
                    label={t('offers') || "En Oferta"}
                    icon={Tag}
                    checked={onSaleOnly}
                    onChange={setOnSaleOnly}
                  />
                </div>

                {/* Categories */}
                <FilterSection 
                  title={t('categories') || "Categorías"}
                  icon={Grid3X3}
                  expanded={expandedSections.categories}
                  onToggle={() => toggleSection('categories')}
                >
                  <div className="category-list">
                    <CheckboxFilter
                      label={t('allCategories') || "Todas las Categorías"}
                      checked={selectedCategories.length === 0}
                      onChange={() => setSelectedCategories([])}
                      count={products.length}
                    />
                    {categories.map((category) => {
                      const categoryName = category.title_translated || category.title;
                      const count = products.filter(p => 
                        p.category?.title === category.title || p.category?.title_translated === categoryName
                      ).length;
                      if (count === 0) return null;
                      return (
                        <CheckboxFilter
                          key={category.id}
                          label={categoryName}
                          checked={selectedCategories.includes(categoryName)}
                          onChange={() => toggleCategory(categoryName)}
                          count={count}
                        />
                      );
                    })}
                  </div>
                </FilterSection>

                {/* Brands */}
                <FilterSection 
                  title={t('brand') || "Marca"}
                  icon={Tag}
                  expanded={expandedSections.brands}
                  onToggle={() => toggleSection('brands')}
                >
                  <div className="category-list">
                    <CheckboxFilter
                      label={t('allBrands') || "Todas las Marcas"}
                      checked={!selectedBrand}
                      onChange={() => setSelectedBrand(null)}
                      count={products.length}
                    />
                    {brands.map((brand) => {
                      const brandName = brand.title_pt || brand.title_es || brand.title;
                      const count = products.filter(p => p.brand?.slug === brand.slug).length;
                      if (count === 0) return null;
                      return (
                        <CheckboxFilter
                          key={brand.id}
                          label={brandName}
                          checked={selectedBrand === brand.slug}
                          onChange={() => setSelectedBrand(selectedBrand === brand.slug ? null : brand.slug)}
                          count={count}
                        />
                      );
                    })}
                  </div>
                </FilterSection>

                {/* Price Range */}
                <FilterSection 
                  title={t('priceRange') || "Precio"}
                  icon={Tag}
                  expanded={expandedSections.price}
                  onToggle={() => toggleSection('price')}
                >
                  <div className="price-range-section">
                    <div className="price-inputs">
                      <div className="price-input-group">
                        <span className="price-currency">$</span>
                        <input
                          type="number"
                          placeholder="0"
                          value={priceRange.min || ''}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, min: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                      <span className="price-separator">—</span>
                      <div className="price-input-group">
                        <span className="price-currency">$</span>
                        <input
                          type="number"
                          placeholder={maxPrice.toString()}
                          value={priceRange.max === maxPrice ? '' : priceRange.max}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, max: parseInt(e.target.value) || maxPrice }))}
                        />
                      </div>
                    </div>
                    <div className="price-presets">
                      <button onClick={() => setPriceRange({ min: 0, max: 50 })}>$0-50</button>
                      <button onClick={() => setPriceRange({ min: 50, max: 100 })}>$50-100</button>
                      <button onClick={() => setPriceRange({ min: 100, max: 500 })}>$100-500</button>
                      <button onClick={() => setPriceRange({ min: 500, max: maxPrice })}>$500+</button>
                    </div>
                  </div>
                </FilterSection>
              </div>
            </aside>

            {/* Main Content */}
            <main className="products-main">
              {/* Results Bar */}
              <div className="results-bar">
                <div className="results-info">
                  <span className="results-count">
                    <strong>{filteredProducts.length}</strong> {t('products') || 'productos'}
                  </span>
                  {hasActiveFilters && (
                    <div className="active-filters-tags">
                      {selectedCategories.map(cat => (
                        <span key={cat} className="filter-tag">
                          {cat}
                          <X size={14} onClick={() => toggleCategory(cat)} />
                        </span>
                      ))}
                      {selectedBrand && (
                        <span className="filter-tag">
                          {brands.find(b => b.slug === selectedBrand)?.title || selectedBrand}
                          <X size={14} onClick={() => setSelectedBrand(null)} />
                        </span>
                      )}
                      {inStockOnly && (
                        <span className="filter-tag">
                          {t('inStock') || 'En Stock'}
                          <X size={14} onClick={() => setInStockOnly(false)} />
                        </span>
                      )}
                      {freeShippingOnly && (
                        <span className="filter-tag">
                          {t('freeShipping') || 'Envío Gratis'}
                          <X size={14} onClick={() => setFreeShippingOnly(false)} />
                        </span>
                      )}
                      {onSaleOnly && (
                        <span className="filter-tag">
                          {t('offers') || 'En Oferta'}
                          <X size={14} onClick={() => setOnSaleOnly(false)} />
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="results-controls">
                  <div className="view-toggle">
                    <button 
                      className={viewMode === 'grid' ? 'active' : ''}
                      onClick={() => setViewMode('grid')}
                    >
                      <LayoutGrid size={18} />
                    </button>
                    <button 
                      className={viewMode === 'list' ? 'active' : ''}
                      onClick={() => setViewMode('list')}
                    >
                      <Grid3X3 size={18} />
                    </button>
                  </div>
                  <select 
                    className="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="relevance">{t('mostRelevant') || 'Más Relevante'}</option>
                    <option value="price-asc">{t('lowestPrice') || 'Precio: Menor a Mayor'}</option>
                    <option value="price-desc">{t('highestPrice') || 'Precio: Mayor a Menor'}</option>
                    <option value="newest">{t('newest') || 'Más Recientes'}</option>
                    <option value="discount">{t('offers') || 'Mayor Descuento'}</option>
                  </select>
                </div>
              </div>

              {/* Products Grid */}
              {loading ? (
                <div className="products-grid">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="product-skeleton">
                      <div className="skeleton-image" />
                      <div className="skeleton-content">
                        <div className="skeleton-line" />
                        <div className="skeleton-line short" />
                        <div className="skeleton-line price" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <Package size={64} />
                  </div>
                  <h3>{t('noProductsFound') || 'No se encontraron productos'}</h3>
                  <p>{t('adjustFilters') || 'Ajusta los filtros para encontrar lo que buscas.'}</p>
                  <button className="clear-filters-btn" onClick={clearFilters}>
                    <X size={18} />
                    {t('clearFilters') || 'Limpiar Filtros'}
                  </button>
                </div>
              ) : (
                <div className={`products-grid ${viewMode}`}>
                  {filteredProducts.map((product) => {
                    const discount = calculateDiscount(product.price, product.old_price);
                    const productTitle = product.title_translated || product.title;
                    const freeShipping = product.shipping_ammount === 0 || product.shipping_ammount === '0.00';
                    const isHovered = hoveredProduct === (product.id || product.pid);

                    return (
                      <div 
                        key={product.id || product.pid}
                        className={`product-card ${isHovered ? 'hovered' : ''}`}
                        onMouseEnter={() => setHoveredProduct(product.id || product.pid)}
                        onMouseLeave={() => {
                          setHoveredProduct(null);
                          handleImageMouseLeave();
                        }}
                      >
                        <Link to={`/detail/${product.slug}`} className="product-link">
                          <div 
                            className="product-image-wrapper"
                            onMouseMove={handleImageMouseMove}
                          >
                            <div className="product-image-bg" />
                            <img
                              src={product.image || '/placeholder.jpg'}
                              alt={productTitle}
                              className="product-image"
                              loading="lazy"
                              style={{
                                transformOrigin: isHovered ? `${zoomOrigin.x}% ${zoomOrigin.y}%` : 'center center'
                              }}
                            />
                            
                            {/* Badges */}
                            <div className="product-badges">
                              {discount > 0 && (
                                <span className="badge-discount">
                                  <Sparkles size={12} />
                                  -{discount}%
                                </span>
                              )}
                              {freeShipping && (
                                <span className="badge-shipping">
                                  <Truck size={12} />
                                  {t('free') || 'Gratis'}
                                </span>
                              )}
                            </div>

                            {/* Quick Actions */}
                            <div className="product-actions">
                              <button 
                                className="action-btn wishlist"
                                onClick={(e) => addToWishlist(e, product)}
                              >
                                <Heart size={18} />
                              </button>
                              <button 
                                className="action-btn cart"
                                onClick={(e) => addToCart(e, product)}
                              >
                                <ShoppingCart size={18} />
                              </button>
                            </div>

                            {/* Quick View Overlay */}
                            <div className="quick-view-overlay">
                              <Eye size={24} />
                              <span>{t('quickView') || 'Ver Producto'}</span>
                            </div>
                          </div>

                          <div className="product-content">
                            <span className="product-category">
                              {product.category?.title_translated || product.category?.title}
                            </span>
                            <h3 className="product-title">{productTitle}</h3>
                            
                            {/* Rating */}
                            <div className="product-rating">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  size={14} 
                                  className={i < (product.rating || 4) ? 'filled' : ''} 
                                />
                              ))}
                              <span className="rating-count">({product.reviews_count || 0})</span>
                            </div>

                            <div className="product-price">
                              <span className="current-price">{formatPrice(product.price)}</span>
                              {product.old_price && discount > 0 && (
                                <span className="old-price">{formatPrice(product.old_price)}</span>
                              )}
                            </div>

                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </main>
          </div>
        </div>

        <style>{`
          .products-page {
            background: #ffffff;
            min-height: 100vh;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          }

          /* Container */
          .products-container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 20px;
          }

          /* Mobile Filter Button */
          .mobile-filter-btn {
            display: none;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            padding: 12px 16px;
            background: white;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 500;
            color: #0f172a;
            cursor: pointer;
            margin-bottom: 16px;
            transition: all 0.2s ease;
          }

          .mobile-filter-btn:hover {
            border-color: #3b82f6;
          }

          .filter-badge {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 20px;
            height: 20px;
            padding: 0 5px;
            background: #3b82f6;
            color: white;
            font-size: 11px;
            font-weight: 600;
            border-radius: 10px;
          }

          /* Mobile Filter Overlay */
          .mobile-filter-overlay {
            display: none;
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.4);
            z-index: 1000;
          }

          .mobile-filter-panel {
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 300px;
            max-width: 85vw;
            background: white;
            display: flex;
            flex-direction: column;
            animation: slideIn 0.25s ease;
          }

          @keyframes slideIn {
            from { transform: translateX(-100%); }
            to { transform: translateX(0); }
          }

          .mobile-filter-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            border-bottom: 1px solid #f1f5f9;
          }

          .mobile-filter-header h3 {
            font-size: 16px;
            font-weight: 600;
            color: #0f172a;
            margin: 0;
          }

          .mobile-filter-header button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            background: #f8fafc;
            border: none;
            border-radius: 8px;
            color: #64748b;
            cursor: pointer;
          }

          .mobile-filter-content {
            flex: 1;
            overflow-y: auto;
            padding: 16px;
          }

          .mobile-filter-footer {
            padding: 16px;
            border-top: 1px solid #f1f5f9;
            display: flex;
            gap: 10px;
          }

          .mobile-filter-footer .clear-btn {
            flex: 1;
            padding: 10px 16px;
            background: #f8fafc;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            color: #64748b;
            cursor: pointer;
          }

          .mobile-filter-footer .apply-btn {
            flex: 1;
            padding: 10px 16px;
            background: #0f172a;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            color: white;
            cursor: pointer;
          }

          /* Layout */
          .products-layout {
            display: flex;
            gap: 32px;
          }

          /* Sidebar */
          .products-sidebar {
            width: 300px;
            flex-shrink: 0;
          }

          .sidebar-card {
            background: white;
            border-radius: 20px;
            border: 1px solid #e2e8f0;
            padding: 24px;
            position: sticky;
            top: 24px;
            box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
          }

          .sidebar-header {
            display: flex;
            align-items: center;
            gap: 10px;
            padding-bottom: 16px;
            border-bottom: 2px solid #f1f5f9;
            margin-bottom: 20px;
          }

          .sidebar-header h2 {
            font-size: 18px;
            font-weight: 700;
            color: #0f172a;
            margin: 0;
            flex: 1;
          }

          .clear-all-btn {
            margin-left: auto;
            background: none;
            border: none;
            color: #3b82f6;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
          }

          /* Search Input */
          .search-input-wrapper {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 14px 16px;
            background: #f8fafc;
            border: 2px solid #e2e8f0;
            border-radius: 14px;
            margin-bottom: 20px;
            transition: all 0.3s ease;
          }

          .search-input-wrapper:focus-within {
            border-color: #3b82f6;
            background: white;
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
          }

          .search-input-wrapper input {
            flex: 1;
            border: none;
            background: transparent;
            font-size: 15px;
            font-weight: 500;
            color: #0f172a;
            outline: none;
          }

          .search-input-wrapper input::placeholder {
            color: #94a3b8;
            font-weight: 400;
          }

          .search-input-wrapper button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            background: #f1f5f9;
            border: none;
            border-radius: 8px;
            color: #64748b;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .search-input-wrapper button:hover {
            background: #e2e8f0;
            color: #0f172a;
          }

          /* Availability Toggles */
          .availability-toggles {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 20px;
          }

          .toggle-filter {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 10px 14px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            font-size: 13px;
            font-weight: 500;
            color: #64748b;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .toggle-filter:hover {
            background: #f1f5f9;
            border-color: #cbd5e1;
          }

          .toggle-filter.active {
            background: #eff6ff;
            border-color: #3b82f6;
            color: #3b82f6;
          }

          /* Filter Section */
          .filter-section {
            margin-bottom: 16px;
          }

          .filter-section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 0;
            cursor: pointer;
            border-bottom: 1px solid #f1f5f9;
          }

          .filter-section-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 15px;
            font-weight: 600;
            color: #0f172a;
          }

          .filter-section-content {
            padding: 16px 0;
          }

          /* Checkbox Filter */
          .checkbox-filter {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 0;
            cursor: pointer;
            transition: background 0.2s ease;
          }

          .checkbox-filter:hover {
            background: #f8fafc;
            margin: 0 -8px;
            padding: 10px 8px;
            border-radius: 8px;
          }

          .checkbox-box {
            width: 20px;
            height: 20px;
            border: 2px solid #d1d5db;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            flex-shrink: 0;
          }

          .checkbox-box.checked {
            background: #3b82f6;
            border-color: #3b82f6;
            color: white;
          }

          .checkbox-label {
            flex: 1;
            font-size: 14px;
            color: #374151;
          }

          .checkbox-count {
            font-size: 12px;
            color: #94a3b8;
            background: #f1f5f9;
            padding: 2px 8px;
            border-radius: 100px;
          }

          /* Price Range */
          .price-range-section {
            padding: 8px 0;
          }

          .price-inputs {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 16px;
          }

          .price-input-group {
            flex: 1;
            display: flex;
            align-items: center;
            background: #f8fafc;
            border: 2px solid #e2e8f0;
            border-radius: 10px;
            overflow: hidden;
            transition: border-color 0.2s ease;
          }

          .price-input-group:focus-within {
            border-color: #3b82f6;
          }

          .price-currency {
            padding: 0 12px;
            color: #64748b;
            font-weight: 500;
          }

          .price-input-group input {
            flex: 1;
            padding: 12px 12px 12px 0;
            border: none;
            background: transparent;
            font-size: 14px;
            color: #0f172a;
            outline: none;
          }

          .price-separator {
            color: #94a3b8;
            font-size: 14px;
            font-weight: 500;
          }

          .price-presets {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .price-presets button {
            padding: 8px 14px;
            background: #f1f5f9;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            color: #64748b;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .price-presets button:hover {
            background: #e2e8f0;
            color: #0f172a;
          }

          /* Main Content */
          .products-main {
            flex: 1;
            min-width: 0;
          }

          /* Results Bar */
          .results-bar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            padding: 12px 16px;
            background: white;
            border-radius: 10px;
            border: 1px solid #f1f5f9;
            margin-bottom: 16px;
            flex-wrap: wrap;
          }

          .results-info {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
          }

          .results-count {
            font-size: 14px;
            color: #64748b;
          }

          .results-count strong {
            color: #0f172a;
            font-weight: 600;
          }

          .active-filters-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
          }

          .filter-tag {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 4px 10px;
            background: #eff6ff;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 500;
            color: #1d4ed8;
          }

          .filter-tag svg {
            cursor: pointer;
            opacity: 0.7;
          }

          .filter-tag svg:hover {
            opacity: 1;
          }

          .results-controls {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .view-toggle {
            display: flex;
            background: #f8fafc;
            border-radius: 8px;
            padding: 3px;
          }

          .view-toggle button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: none;
            border: none;
            border-radius: 6px;
            color: #94a3b8;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .view-toggle button.active {
            background: white;
            color: #0f172a;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
          }

          .sort-select {
            padding: 8px 12px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            color: #0f172a;
            cursor: pointer;
            outline: none;
          }

          .sort-select:focus {
            border-color: #3b82f6;
          }

          /* Products Grid */
          .products-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 16px;
          }

          .products-grid.list {
            grid-template-columns: 1fr;
          }

          /* Product Card */
          .product-card {
            background: white;
            border-radius: 16px;
            overflow: hidden;
            border: 1px solid #f1f5f9;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            flex-direction: column;
          }

          .product-card:hover {
            transform: translateY(-6px);
            box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
            border-color: #e2e8f0;
          }

          .product-link {
            text-decoration: none;
            color: inherit;
            display: flex;
            flex-direction: column;
            flex: 1;
          }

          .product-image-wrapper {
            position: relative;
            aspect-ratio: 1;
            overflow: hidden;
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          }

          .product-image-bg {
            position: absolute;
            inset: 0;
            background: radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.04) 0%, transparent 50%);
          }

          .product-image {
            width: 100%;
            height: 100%;
            object-fit: contain;
            padding: 16px;
            transition: transform 0.2s ease-out;
          }

          .product-card:hover .product-image {
            transform: scale(1.25);
          }

          /* Badges */
          .product-badges {
            position: absolute;
            top: 8px;
            left: 8px;
            display: flex;
            flex-direction: column;
            gap: 4px;
            z-index: 2;
          }

          .badge-discount {
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 4px 8px;
            background: #ef4444;
            color: white;
            font-size: 11px;
            font-weight: 600;
            border-radius: 6px;
          }

          .badge-shipping {
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 4px 8px;
            background: #10b981;
            color: white;
            font-size: 11px;
            font-weight: 600;
            border-radius: 6px;
          }

          /* Quick Actions */
          .product-actions {
            position: absolute;
            top: 8px;
            right: 8px;
            display: flex;
            flex-direction: column;
            gap: 4px;
            z-index: 3;
            opacity: 0;
            transform: translateX(8px);
            transition: all 0.2s ease;
          }

          .product-card:hover .product-actions {
            opacity: 1;
            transform: translateX(0);
          }

          .action-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: white;
            border: none;
            border-radius: 8px;
            color: #64748b;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
          }

          .action-btn:hover {
            transform: scale(1.1);
          }

          .action-btn.wishlist:hover {
            background: #fee2e2;
            color: #ef4444;
          }

          .action-btn.cart:hover {
            background: #0f172a;
            color: white;
          }

          /* Quick View Overlay */
          .quick-view-overlay {
            display: none;
          }

          /* Product Content */
          .product-content {
            padding: 16px;
            display: flex;
            flex-direction: column;
            gap: 6px;
            flex: 1;
          }

          .product-category {
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #3b82f6;
            background: #eff6ff;
            padding: 4px 8px;
            border-radius: 4px;
            width: fit-content;
          }

          .product-title {
            font-size: 14px;
            font-weight: 600;
            color: #0f172a;
            margin: 0;
            line-height: 1.4;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          /* Rating */
          .product-rating {
            display: flex;
            align-items: center;
            gap: 3px;
          }

          .product-rating svg {
            color: #e2e8f0;
            width: 14px;
            height: 14px;
          }

          .product-rating svg.filled {
            color: #fbbf24;
            fill: #fbbf24;
          }

          .rating-count {
            font-size: 12px;
            color: #64748b;
            font-weight: 500;
            margin-left: 4px;
          }

          /* Price */
          .product-price {
            display: flex;
            align-items: baseline;
            gap: 8px;
            margin-top: auto;
            padding-top: 8px;
          }

          .current-price {
            font-size: 18px;
            font-weight: 700;
            color: #0f172a;
          }

          .old-price {
            font-size: 13px;
            color: #94a3b8;
            text-decoration: line-through;
          }

          /* Empty State */
          .empty-state {
            text-align: center;
            padding: 60px 20px;
            background: white;
            border-radius: 12px;
            border: 1px solid #f1f5f9;
          }

          .empty-icon {
            width: 72px;
            height: 72px;
            background: #f8fafc;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
            color: #94a3b8;
          }

          .empty-state h3 {
            font-size: 18px;
            font-weight: 600;
            color: #0f172a;
            margin: 0 0 6px;
          }

          .empty-state p {
            font-size: 14px;
            color: #64748b;
            margin: 0 0 20px;
          }

          .clear-filters-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 10px 20px;
            background: #0f172a;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .clear-filters-btn:hover {
            background: #1e293b;
          }

          /* Skeleton Loading */
          .product-skeleton {
            background: white;
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid #f1f5f9;
          }

          .skeleton-image {
            aspect-ratio: 1;
            background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
          }

          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }

          .skeleton-content {
            padding: 12px;
          }

          .skeleton-line {
            height: 12px;
            background: #f1f5f9;
            border-radius: 4px;
            margin-bottom: 8px;
          }

          .skeleton-line.short {
            width: 60%;
          }

          .skeleton-line.price {
            height: 16px;
            width: 40%;
          }

          /* Responsive */
          @media (max-width: 1024px) {
            .products-sidebar {
              display: none;
            }

            .mobile-filter-btn {
              display: flex;
            }

            .mobile-filter-overlay {
              display: block;
            }
          }

          @media (max-width: 768px) {
            .products-container {
              padding: 12px;
            }

            .results-bar {
              padding: 12px;
              flex-direction: column;
              gap: 12px;
            }

            .results-controls {
              width: 100%;
              justify-content: space-between;
            }

            .products-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
            }

            .product-content {
              padding: 10px;
            }

            .product-title {
              font-size: 12px;
            }

            .current-price {
              font-size: 14px;
            }

            .product-actions {
              display: none;
            }
          }

          @media (max-width: 480px) {
            .products-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 8px;
            }

            .product-content {
              padding: 8px;
            }

            .product-category {
              font-size: 9px;
            }

            .product-title {
              font-size: 11px;
              margin-bottom: 4px;
            }

            .product-rating {
              margin-bottom: 4px;
            }

            .current-price {
              font-size: 13px;
            }

            .old-price {
              font-size: 10px;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default Products;
